import {
  allocationCheck,
  cohortNumbers,
  defaultInvoiceDate,
  deliveryPhase,
  deliveryResult,
  itemCodeFor,
  message,
  planInvoices,
  resultPerSeat,
  whyNothingToInvoice,
} from '@domain';
import type { LogEntry, QueueEntry, ReferenceData, TenantConnection } from '@domain';

/**
 * Development fixtures — local only.
 *
 * Every organisation, contact and reference below is invented. The design
 * prototype used real Qatari company names as filler; those are replaced here
 * so no client-identifying name reaches a repo, a screenshot or a demo.
 *
 * These exist so the screens can be built and reviewed before Entra External ID
 * and the Xero app registration are in place. They are never served to a
 * deployed site: the API is the only source of data there.
 */

/*
  Connection ids match the company ids the session hands out (7 upwards).
  They are the same thing: the browser is never told a real Xero tenant id,
  so the connections list carries our own org id in its place. A fixture
  using 't1'..'t5' could not show the header badge at all.
*/
export const TENANTS: TenantConnection[] = [
  {
    id: '7',
    name: 'Hero Factory',
    tenantId: 'b3f1c0e2-7a45-4d19-9c88-51e0a2d7f3aa',
    age: 12,
    lastSync: '09:14',
    lastSyncSub: 'Today',
    users: 3,
    state: 'OK',
  },
  {
    id: '8',
    name: 'Harbour Marine Services',
    tenantId: '7c20a6d4-1f38-4b52-a0e6-9b41c8ee2d17',
    age: 49,
    lastSync: '08:02',
    lastSyncSub: 'Today',
    users: 2,
    state: 'EXPIRING',
  },
  {
    id: '9',
    name: 'Southport Cold Store',
    tenantId: 'e91d4488-6c0a-47f1-8de3-24b7a05f9c66',
    age: 4,
    lastSync: '07:48',
    lastSyncSub: 'Today',
    users: 1,
    state: 'OK',
  },
  {
    id: '10',
    name: 'Skyline Interiors',
    tenantId: '2ab8f70c-9d31-4e6b-b114-58c9d3a71e02',
    age: 60,
    lastSync: '2 Sep',
    lastSyncSub: '11 days ago',
    users: 2,
    state: 'REAUTH',
  },
  {
    id: '11',
    name: 'Delta Paper Trading',
    tenantId: '58e3b19f-4c72-4a08-91dd-6f2e07cb45b3',
    age: 33,
    lastSync: '12 Sep',
    lastSyncSub: 'Refresh failed at 22:41',
    users: 1,
    state: 'FAILED',
  },
];

export const REFERENCE: ReferenceData = {
  accounts: [
    /*
      Two bank accounts, because one is not enough to try a transfer with and
      a screen that cannot be tried is a screen nobody checked. Current
      account and petty cash is also the arrangement almost every small
      company in Doha actually has.
    */
    { code: '090', name: 'الحساب الجاري', description: 'Business current account', type: 'BANK' },
    { code: '091', name: 'الصندوق النثري', description: 'Petty cash', type: 'BANK' },
    { code: '200', name: 'المبيعات', description: 'Sales', type: 'REVENUE' },
    { code: '260', name: 'إيرادات أخرى', description: 'Other revenue', type: 'REVENUE' },
    { code: '310', name: 'تكلفة البضاعة المباعة', description: 'Cost of goods sold', type: 'DIRECTCOSTS' },
    { code: '400', name: 'الدعاية والإعلان', description: 'Advertising', type: 'EXPENSE' },
    { code: '404', name: 'رسوم بنكية', description: 'Bank fees', type: 'EXPENSE' },
    { code: '420', name: 'مصروفات عامة', description: 'General expenses', type: 'EXPENSE' },
    { code: '429', name: 'مستهلكات المطبخ', description: 'Kitchen consumables', type: 'EXPENSE' },
    { code: '630', name: 'المخزون', description: 'Inventory', type: 'INVENTORY' },
  ],
  taxRates: [
    { type: 'NONE', name: 'Tax exempt (0%)', rate: 0 },
    { type: 'OUTPUT', name: 'Tax on sales (5%)', rate: 5 },
    { type: 'INPUT', name: 'Tax on purchases (5%)', rate: 5 },
  ],
  /*
    Account codes included, because Xero sends them and the line cannot be
    filed without one. SRV-CTR deliberately has no purchase account: an item
    set up only to be sold is the ordinary case, and choosing it on a bill
    must leave the account empty rather than inheriting the last item's.
  */
  items: [
    { code: 'CHK-BRST', name: 'Chicken breast, boneless (kg)', description: '', sell: 26.5, buy: 18.2, sellAccount: '200', buyAccount: '310' },
    { code: 'RCE-BAS', name: 'Basmati rice, 20kg sack', description: '', sell: 148, buy: 112, sellAccount: '200', buyAccount: '310' },
    { code: 'OIL-SUN', name: 'Sunflower oil, 18L', description: '', sell: 210, buy: 168, sellAccount: '200', buyAccount: '310' },
    { code: 'PKG-BOX', name: 'Meal box, kraft (500 pcs)', description: '', sell: 320, buy: 245, sellAccount: '260', buyAccount: '429' },
    { code: 'SRV-CTR', name: 'Catering service, per head', description: '', sell: 85, buy: 0, sellAccount: '260', buyAccount: '' },
  ],
  currencies: ['QAR', 'USD', 'AED', 'EUR'],
  contacts: [
    { id: 'c1', name: 'Corniche Hospitality Group' },
    { id: 'c2', name: 'Westbay Properties' },
    { id: 'c3', name: 'Skyline Inflight Catering' },
    { id: 'c4', name: 'Pearl Marina Club' },
    { id: 'c5', name: 'Souq Al Noor Consumer Goods' },
    { id: 'c6', name: 'Capital Poultry Trading' },
    { id: 'c7', name: 'Delta Packaging Industries' },
    { id: 'c8', name: 'Meridian Facilities Management' },
  ],
};

/** References already in Xero, used by the duplicate pre-push check. */
export const KNOWN_REFERENCES = ['SUP-77412', 'GPI-2209', 'PO 88213'];

export const QUEUE: QueueEntry[] = [
  {
    id: 'q1',
    type: 'Invoice',
    doc: 'INV-0242',
    contact: 'Corniche Hospitality Group',
    amount: 18450,
    endpoint: 'PUT /Invoices',
    state: 'READY',
    note: message('checks_passed'),
  },
  {
    id: 'q2',
    type: 'Bill',
    doc: 'SUP-77419',
    contact: 'Capital Poultry Trading',
    amount: 24380,
    endpoint: 'PUT /Invoices',
    state: 'REVIEW',
    note: message('duplicate_reference', { reference: 'SUP-77412' }),
  },
  {
    id: 'q3',
    type: 'Purchase order',
    doc: 'PO-0139',
    contact: 'Capital Poultry Trading',
    amount: 31200,
    endpoint: 'PUT /PurchaseOrders',
    state: 'BLOCKED',
    note: message('line_missing_account', { line: 2 }),
  },
  {
    id: 'q4',
    type: 'Quote',
    doc: 'QU-0068',
    contact: 'Pearl Marina Club',
    amount: 154000,
    endpoint: 'PUT /Quotes',
    state: 'READY',
    note: message('checks_passed'),
  },
];

export const LOG: LogEntry[] = [
  {
    id: 'l1',
    time: '09:41:22',
    doc: 'INV-0241',
    endpoint: 'PUT /Invoices',
    http: '200',
    msg: 'Created — InvoiceID 4f8c1e02-7ab9-4c31-9d18-77e2a1b6c9d0 · Status DRAFT',
    ok: true,
  },
  {
    id: 'l2',
    time: '09:39:07',
    doc: 'SUP-77412',
    endpoint: 'PUT /Invoices',
    http: '200',
    msg: 'Created — InvoiceID a1b2c3d4-55e6-4f70-8a91-0c2d3e4f5a6b · Status DRAFT',
    ok: true,
  },
  {
    id: 'l3',
    time: '09:36:44',
    doc: 'PO-0138',
    endpoint: 'PUT /PurchaseOrders',
    http: '400',
    msg: 'ValidationErrors: Account code 999 is not valid for this organisation',
    ok: false,
  },
  {
    id: 'l4',
    time: '09:31:18',
    doc: 'QU-0067',
    endpoint: 'PUT /Quotes',
    http: '429',
    msg: 'Rate limited — Retry-After 12s',
    ok: false,
  },
  {
    id: 'l5',
    time: '09:22:53',
    doc: 'CUS-0021',
    endpoint: 'PUT /Contacts',
    http: '200',
    msg: 'Created — ContactID 8de41f77-2c05-49aa-b3f1-6d0e7c95a412',
    ok: true,
  },
];

type ListRow = (string | number)[];

export interface ListFixture {
  title: string;
  root: string;
  newLabel: string;
  newScreen: string;
  views: string[];
  columns: {
    key: string;
    label: string;
    w: string;
    mono?: boolean;
    muted?: boolean;
    link?: boolean;
    align?: 'end' | 'center';
    pill?: boolean;
  }[];
  rows: ListRow[];
  /** The Xero identifier for each row, in the same order as `rows`. */
  ids?: string[];
}

export const LISTS: Record<string, ListFixture> = {
  invoices: {
    title: 'Invoices',
    root: 'Sales',
    newLabel: 'New invoice',
    newScreen: 'form:invoice',
    views: ['All', 'Overdue', 'Awaiting payment', 'Part paid', 'Draft', 'Paid'],
    // Invented identifiers, so the print and PDF actions appear during
    // development instead of being silently hidden. They point at nothing real:
    // asking Xero for one would fail, which is correct, because these documents
    // do not exist there either.
    ids: [
      '00000000-0000-4000-8000-00000000a001',
      '00000000-0000-4000-8000-00000000a002',
      '00000000-0000-4000-8000-00000000a003',
      '00000000-0000-4000-8000-00000000a004',
      '00000000-0000-4000-8000-00000000a005',
      '00000000-0000-4000-8000-00000000a006',
      '00000000-0000-4000-8000-00000000a007',
      '00000000-0000-4000-8000-00000000a008',
    ],
    columns: [
      { key: 'no', label: 'Number', w: '120px', mono: true, link: true },
      { key: 'contact', label: 'Contact', w: 'minmax(0,1fr)' },
      { key: 'ref', label: 'Reference', w: '150px', muted: true },
      { key: 'date', label: 'Date', w: '100px', mono: true, muted: true },
      { key: 'due', label: 'Due date', w: '100px', mono: true, muted: true },
      { key: 'amount', label: 'Amount', w: '110px', mono: true, align: 'end' },
      { key: 'outstanding', label: 'Outstanding', w: '120px', mono: true, align: 'end' },
      { key: 'status', label: 'Status', w: '150px', align: 'end', pill: true },
    ],
    /*
      One of each state the real list can show, including a part-paid invoice
      that is also overdue. Every row used to read IN XERO, which is precisely
      why nobody noticed the screen was answering the wrong question.
    */
    rows: [
      ['INV-0241', 'Corniche Hospitality Group', 'PO 88213', '2026-09-04', '2026-10-04', 18450, 18450, 'DRAFT'],
      ['INV-0240', 'Skyline Inflight Catering', 'Sept meals wk1', '2026-09-03', '2026-09-18', 96320.5, 96320.5, 'OVERDUE'],
      ['INV-0239', 'Westbay Properties', 'Cafeteria — Aug', '2026-09-01', '2026-09-16', 42100, 12100, 'OVERDUE'],
      ['INV-0238', 'Pearl Marina Club', 'Event 4 Sept', '2026-08-30', '2026-12-14', 12750, 4750, 'PART PAID'],
      ['INV-0237', 'Souq Al Noor Consumer Goods', 'Wholesale 34', '2026-08-28', '2026-12-27', 63400, 0, 'PAID'],
      ['INV-0236', 'Corniche Hospitality Group', 'PO 88044', '2026-08-24', '2026-09-23', 22980, 0, 'PAID'],
      ['INV-0235', 'Meridian Facilities Management', 'Staff meals', '2026-08-21', '2026-11-20', 31600, 31600, 'AWAITING APPROVAL'],
      ['INV-0234', 'Skyline Inflight Catering', 'Aug wk4', '2026-08-18', '2026-09-02', 88240, 0, 'VOIDED'],
    ],
  },
  bills: {
    title: 'Bills',
    root: 'Purchases',
    newLabel: 'New bill',
    newScreen: 'form:bill',
    views: ['All', 'Overdue', 'Awaiting payment', 'Part paid', 'Draft', 'Paid'],
    ids: [
      '00000000-0000-4000-8000-00000000b001',
      '00000000-0000-4000-8000-00000000b002',
      '00000000-0000-4000-8000-00000000b003',
    ],
    columns: [
      { key: 'no', label: 'Ref', w: '130px', mono: true, link: true },
      { key: 'contact', label: 'From', w: 'minmax(0,1fr)' },
      { key: 'date', label: 'Date', w: '100px', mono: true, muted: true },
      { key: 'due', label: 'Due date', w: '100px', mono: true, muted: true },
      { key: 'amount', label: 'Amount', w: '110px', mono: true, align: 'end' },
      { key: 'outstanding', label: 'Outstanding', w: '120px', mono: true, align: 'end' },
      { key: 'status', label: 'Status', w: '150px', align: 'end', pill: true },
    ],
    rows: [
      ['SUP-77412', 'Capital Poultry Trading', '2026-09-04', '2026-09-19', 24380, 24380, 'DRAFT'],
      ['GPI-2209', 'Delta Packaging Industries', '2026-09-02', '2026-10-02', 9820, 9820, 'OVERDUE'],
      ['AM-55102', 'Souq Al Noor Consumer Goods', '2026-08-31', '2026-09-30', 15240.75, 5240.75, 'OVERDUE'],
      ['SUP-77380', 'Capital Poultry Trading', '2026-08-27', '2026-12-11', 22110, 9110, 'PART PAID'],
      ['BFM-3391', 'Meridian Facilities Management', '2026-08-25', '2026-09-24', 6500, 0, 'PAID'],
      ['GPI-2194', 'Delta Packaging Industries', '2026-08-19', '2026-09-18', 11400, 0, 'PAID'],
    ],
  },
  quotes: {
    title: 'Quotes',
    root: 'Sales',
    newLabel: 'New quote',
    newScreen: 'form:quote',
    views: ['All', 'Draft', 'Queued', 'In Xero', 'Failed'],
    columns: [
      { key: 'no', label: 'Number', w: '120px', mono: true, link: true },
      { key: 'contact', label: 'Contact', w: 'minmax(0,1fr)' },
      { key: 'title', label: 'Title', w: 'minmax(0,1fr)', muted: true },
      { key: 'date', label: 'Issued', w: '100px', mono: true, muted: true },
      { key: 'expiry', label: 'Expires', w: '100px', mono: true, muted: true },
      { key: 'amount', label: 'Amount', w: '120px', mono: true, align: 'end' },
      { key: 'status', label: 'Status', w: '112px', align: 'end', pill: true },
    ],
    rows: [
      ['QU-0068', 'Pearl Marina Club', 'Ramadan buffet 2027', '2026-09-04', '2026-09-25', 154000, 'DRAFT'],
      ['QU-0067', 'Westbay Properties', 'Cafeteria retender', '2026-09-01', '2026-09-22', 486000, 'IN XERO'],
      ['QU-0066', 'Corniche Hospitality Group', 'Gala dinner, 400 pax', '2026-08-28', '2026-09-11', 118500, 'IN XERO'],
      ['QU-0065', 'Meridian Facilities Management', 'Staff meals Q4', '2026-08-20', '2026-09-03', 92400, 'FAILED'],
    ],
  },
  pos: {
    title: 'Purchase orders',
    root: 'Purchases',
    newLabel: 'New purchase order',
    newScreen: 'form:po',
    views: ['All', 'Draft', 'Queued', 'In Xero', 'Failed'],
    columns: [
      { key: 'no', label: 'Order', w: '120px', mono: true, link: true },
      { key: 'contact', label: 'Supplier', w: 'minmax(0,1fr)' },
      { key: 'date', label: 'Raised', w: '100px', mono: true, muted: true },
      { key: 'delivery', label: 'Delivery', w: '100px', mono: true, muted: true },
      { key: 'amount', label: 'Amount', w: '120px', mono: true, align: 'end' },
      { key: 'status', label: 'Status', w: '142px', align: 'end', pill: true },
    ],
    rows: [
      ['PO-0139', 'Capital Poultry Trading', '2026-09-05', '2026-09-08', 31200, 'DRAFT'],
      ['PO-0138', 'Delta Packaging Industries', '2026-09-03', '2026-09-14', 12800, 'REVIEW'],
      ['PO-0137', 'Souq Al Noor Consumer Goods', '2026-08-30', '2026-09-04', 18650, 'IN XERO'],
      ['PO-0136', 'Capital Poultry Trading', '2026-08-26', '2026-08-29', 27400, 'IN XERO'],
    ],
  },
  customers: {
    title: 'Customers',
    root: 'Sales',
    newLabel: 'New contact',
    newScreen: 'form:contact',
    views: ['All', 'Draft', 'In Xero'],
    columns: [
      { key: 'name', label: 'Contact name', w: 'minmax(0,1fr)', link: true },
      { key: 'acct', label: 'Account no.', w: '130px', mono: true, muted: true },
      { key: 'email', label: 'Email', w: 'minmax(0,1fr)', muted: true },
      { key: 'cur', label: 'Currency', w: '90px', mono: true },
      { key: 'status', label: 'Xero', w: '112px', align: 'end', pill: true },
    ],
    rows: [
      ['Corniche Hospitality Group', 'CUS-0012', 'ap@corniche.example', 'QAR', 'IN XERO'],
      ['Skyline Inflight Catering', 'CUS-0004', 'finance@skyline.example', 'QAR', 'IN XERO'],
      ['Westbay Properties', 'CUS-0009', 'payables@westbay.example', 'QAR', 'IN XERO'],
      ['Pearl Marina Club', 'CUS-0021', 'accounts@pearlmarina.example', 'QAR', 'IN XERO'],
      ['Northgate Festival Mall', 'CUS-0033', 'ap@northgate.example', 'QAR', 'DRAFT'],
    ],
  },
  suppliers: {
    title: 'Suppliers',
    root: 'Purchases',
    newLabel: 'New contact',
    newScreen: 'form:contact',
    views: ['All', 'Draft', 'In Xero'],
    columns: [
      { key: 'name', label: 'Contact name', w: 'minmax(0,1fr)', link: true },
      { key: 'acct', label: 'Account no.', w: '130px', mono: true, muted: true },
      { key: 'tax', label: 'Tax ID', w: '140px', mono: true, muted: true },
      { key: 'cur', label: 'Currency', w: '90px', mono: true },
      { key: 'status', label: 'Xero', w: '112px', align: 'end', pill: true },
    ],
    rows: [
      ['Capital Poultry Trading', 'SUP-0007', 'QA-5540182', 'QAR', 'IN XERO'],
      ['Delta Packaging Industries', 'SUP-0011', 'QA-6612907', 'QAR', 'IN XERO'],
      ['Souq Al Noor Consumer Goods', 'SUP-0002', 'QA-1120044', 'QAR', 'IN XERO'],
      ['Meridian Facilities Management', 'SUP-0015', 'QA-8890231', 'QAR', 'DRAFT'],
    ],
  },
  items: {
    title: 'Products and services',
    root: 'Sales',
    newLabel: 'New item',
    newScreen: 'form:item',
    views: ['All', 'Tracked', 'Untracked'],
    columns: [
      { key: 'code', label: 'Code', w: '130px', mono: true, link: true },
      { key: 'name', label: 'Name', w: 'minmax(0,1fr)' },
      { key: 'tracked', label: 'Tracked', w: '100px', align: 'center', pill: true },
      { key: 'buy', label: 'Cost price', w: '110px', mono: true, align: 'end' },
      { key: 'sell', label: 'Sale price', w: '110px', mono: true, align: 'end' },
      { key: 'onhand', label: 'On hand', w: '100px', mono: true, align: 'end' },
    ],
    rows: [
      ['CHK-BRST', 'Chicken breast, boneless (kg)', 'TRACKED', 18.2, 26.5, 412],
      ['RCE-BAS', 'Basmati rice, 20kg sack', 'TRACKED', 112, 148, 88],
      ['OIL-SUN', 'Sunflower oil, 18L', 'TRACKED', 168, 210, 54],
      ['PKG-BOX', 'Meal box, kraft (500 pcs)', 'TRACKED', 245, 320, 26],
      ['SRV-CTR', 'Catering service, per head', 'UNTRACKED', 0, 85, 0],
    ],
  },
  inventory: {
    title: 'Inventory',
    root: 'Inventory',
    newLabel: '',
    newScreen: '',
    views: ['All locations', 'Hero Factory', 'Central store'],
    columns: [
      { key: 'code', label: 'Code', w: '130px', mono: true, link: true },
      { key: 'name', label: 'Item', w: 'minmax(0,1fr)' },
      { key: 'loc', label: 'Location', w: '160px', muted: true },
      { key: 'onhand', label: 'On hand', w: '100px', mono: true, align: 'end' },
      { key: 'value', label: 'Asset value', w: '130px', mono: true, align: 'end' },
      { key: 'status', label: 'Level', w: '120px', align: 'end', pill: true },
    ],
    rows: [
      ['CHK-BRST', 'Chicken breast, boneless (kg)', 'Hero Factory', 412, 7498.4, 'OK'],
      ['RCE-BAS', 'Basmati rice, 20kg sack', 'Central store', 88, 9856, 'OK'],
      ['OIL-SUN', 'Sunflower oil, 18L', 'Central store', 54, 9072, 'OK'],
      ['PKG-BOX', 'Meal box, kraft (500 pcs)', 'Hero Factory', 26, 6370, 'LOW'],
      ['SPC-MIX', 'Spice mix, house blend (kg)', 'Hero Factory', 4, 320, 'REORDER'],
    ],
  },
  stock: {
    title: "What's in stock?",
    root: 'Inventory',
    newLabel: '',
    newScreen: '',
    views: ['Needs attention', 'All'],
    columns: [
      { key: 'name', label: 'Item', w: 'minmax(0,1fr)' },
      { key: 'onhand', label: 'On hand', w: '100px', mono: true, align: 'end' },
      { key: 'reorder', label: 'Reorder at', w: '110px', mono: true, align: 'end', muted: true },
      { key: 'cover', label: 'Days cover', w: '110px', mono: true, align: 'end' },
      { key: 'status', label: 'Level', w: '120px', align: 'end', pill: true },
    ],
    rows: [
      ['Spice mix, house blend (kg)', 4, 15, 2, 'REORDER'],
      ['Meal box, kraft (500 pcs)', 26, 30, 6, 'LOW'],
      ['Sunflower oil, 18L', 54, 40, 19, 'OK'],
      ['Basmati rice, 20kg sack', 88, 50, 31, 'OK'],
      ['Chicken breast, boneless (kg)', 412, 250, 11, 'OK'],
    ],
  },
};

/**
 * One document as Xero returns it, so the printed sheet can be looked at during
 * development. Invented, like everything else here: a caterer billing a hotel,
 * with descriptions in both languages because that is the case the layout
 * exists to handle.
 */
export const DOCUMENT_DETAIL = {
  organisation: 'Hero Factory',
  document: {
    Type: 'ACCREC',
    Status: 'AUTHORISED',
    InvoiceNumber: 'INV-0240',
    Reference: 'Sept meals wk1',
    DateString: '2026-09-03T00:00:00',
    DueDateString: '2026-09-18T00:00:00',
    CurrencyCode: 'QAR',
    SubTotal: 91734.76,
    TotalTax: 4585.74,
    Total: 96320.5,
    AmountPaid: 0,
    AmountDue: 96320.5,
    Contact: { Name: 'Skyline Inflight Catering', EmailAddress: 'accounts@example.qa' },
    LineItems: [
      {
        Description: 'وجبات الطاقم — الأسبوع الأول من سبتمبر',
        Quantity: 1840,
        UnitAmount: 32.5,
        LineAmount: 59800,
        AccountCode: '200',
        TaxAmount: 2990,
      },
      {
        Description: 'Chilled meal transport, Doha airside',
        Quantity: 22,
        UnitAmount: 985,
        LineAmount: 21670,
        AccountCode: '200',
        TaxAmount: 1083.5,
      },
      {
        Description: 'رسوم التغليف / Packaging surcharge',
        Quantity: 1,
        UnitAmount: 10264.76,
        LineAmount: 10264.76,
        AccountCode: '260',
        TaxAmount: 512.24,
      },
    ],
  },
};

/** What a company that has filled in its own footer looks like. */
export const PRINT_BRANDING = {
  hasLogo: false,
  footer: {
    email: 'accounts@herofactory.example.qa',
    phone: '+974 4000 1122',
    address: 'Street 12, Industrial Area, Doha, Qatar',
  },
};

/**
 * The branding record behind the admin screen.
 *
 * Set to the banded layout so both templates can be looked at during
 * development. The bank details are invented, as everything here is: a real
 * account number does not belong in source control, and this file is read by
 * anyone who clones the repository.
 */
export const BRANDING = {
  organisation: 'Hero Factory',
  hasLogo: false,
  logoUrl: null,
  contentType: null,
  fileName: null,
  uploadedAt: null,
  bytes: 0,
  footer: PRINT_BRANDING.footer,
  presentation: {
    template: 'banded',
    taglineAr: 'نُطوّر المهارات،\nونصنع القادة ..',
    taglineEn: 'We build skills,\nand we make leaders.',
    accentColour: '#9b2242',
    bandColour: '#fbe9cf',
    bank: {
      name: 'Example Bank',
      accountName: 'HERO FACTORY TRADING WLL',
      accountNumber: '0000000000000',
      iban: 'QA00EXMP000000000000000000000',
    },
    terms:
      'Payment in full is due on completion, by transfer to the account above. ' +
      'The account details printed here are the only ones to be used.',
  },
};

/** The catalogue, and what this invented company uses. */
export const PAYMENT_METHODS: {
  organisation: string;
  catalogue: { code: string; en: string; ar: string }[];
  chosen: string[];
  defaultMethod: string | null;
} = {
  organisation: 'Hero Factory',
  catalogue: [
    { code: 'bank_transfer', en: 'Bank transfer', ar: 'تحويل بنكي' },
    { code: 'cash', en: 'Cash', ar: 'نقداً' },
    { code: 'cheque', en: 'Cheque', ar: 'شيك' },
    { code: 'card', en: 'Card', ar: 'بطاقة' },
    { code: 'pos', en: 'Card machine', ar: 'جهاز نقاط البيع' },
    { code: 'online', en: 'Online payment', ar: 'دفع إلكتروني' },
    { code: 'other', en: 'Other', ar: 'أخرى' },
  ],
  chosen: ['bank_transfer', 'cash', 'cheque'],
  defaultMethod: 'bank_transfer',
};

/**
 * What the payment form is offered. Invented invoices against invented
 * accounts, so the form can be worked through without a Xero connection.
 */
export const PAYMENT_OPTIONS = {
  invoices: [
    { id: '00000000-0000-4000-8000-00000000a240', number: 'INV-0240', reference: 'Sept meals wk1', contact: 'Skyline Inflight Catering', due: '2026-09-18', amountDue: 96320.5, total: 96320.5, direction: 'in' as const, currency: 'QAR' },
    { id: '00000000-0000-4000-8000-00000000a237', number: 'INV-0237', reference: 'Wholesale 34', contact: 'Souq Al Noor Consumer Goods', due: '2026-09-27', amountDue: 63400, total: 63400, direction: 'in' as const, currency: 'QAR' },
  ],
  accounts: [
    // Typed, because the payment screens now say which kind each is —
    // "which of these is my bank?" being the question asked there.
    { code: '090', name: 'Business Current Account', type: 'BANK' },
    { code: '091', name: 'Petty Cash', type: 'BANK' },
  ],
  currency: 'QAR',
  methods: PAYMENT_METHODS.catalogue.filter((m) => PAYMENT_METHODS.chosen.includes(m.code)),
  defaultMethod: PAYMENT_METHODS.defaultMethod,
};

/**
 * A file as it arrives: headings in two languages, one row missing a name and
 * one supplier listed twice, so the preview has something to object to.
 */
/**
 * Who can reach the demo company.
 *
 * Deliberately one of each: somebody signed in, somebody whose sign-in is
 * ready and waiting, somebody the directory refused, and somebody invited
 * under the old arrangement where no account was made at all. The screen has
 * to say something different and true about each, and that is hard to check
 * against a list where they all look the same.
 *
 * Invented people at an invented company, as everything in this file is.
 */
export const PEOPLE = {
  organisation: 'Al Nahda Trading WLL',
  grantableRoles: ['client_user', 'client_admin'] as const,
  createsSignIn: true,
  // As a client's own admin sees it: they manage colleagues, not accountants.
  canRemoveFirmMembers: false,
  members: [
    {
      userId: 4,
      email: 'accountant@cfoai.example',
      displayName: 'Your accountant',
      role: 'cfoai_staff' as const,
      since: '2026-01-06T08:00:00Z',
      lastSeen: '2026-10-10T15:20:00Z',
      canSetPassword: false,
    },
    {
      userId: 1,
      email: 'noora@alnahda.example',
      displayName: 'Noora Al-Sulaiti',
      role: 'client_admin' as const,
      since: '2026-02-11T08:00:00Z',
      lastSeen: '2026-10-09T06:41:00Z',
      canSetPassword: true,
    },
  ],
  invited: [
    {
      invitationId: 91,
      email: 'khalid@alnahda.example',
      displayName: 'Khalid Al-Marri',
      role: 'client_user' as const,
      invitedAt: '2026-10-08T09:12:00Z',
      accountState: 'created' as const,
      accountDetail: null,
      canSetPassword: true,
    },
    {
      invitationId: 92,
      email: 'accounts@alnahda.example',
      displayName: null,
      role: 'client_user' as const,
      invitedAt: '2026-10-09T11:02:00Z',
      accountState: 'refused' as const,
      accountDetail: '400: The domain portion of the address is not accepted.',
      canSetPassword: false,
    },
    {
      invitationId: 93,
      email: 'maryam@alnahda.example',
      displayName: null,
      role: 'client_user' as const,
      invitedAt: '2026-05-04T07:30:00Z',
      accountState: null,
      accountDetail: null,
      canSetPassword: false,
    },
  ],
};

export const CONTACT_IMPORT_PREVIEW = {
  headings: ['Name · الاسم', 'Email · البريد الإلكتروني', 'Phone · الهاتف', 'City · المدينة'],
  columns: { name: 0, email: 1, phone: 2, city: 3 },
  encoding: 'utf-8' as string | null,
  raw: [
    ['Al Noor Trading WLL', 'accounts@example.qa', '+974 4000 1100', 'Doha'],
    ['مؤسسة الفجر للتجارة', 'info@example.qa', '+974 4000 1200', 'الدوحة'],
    ['', 'nobody@example.qa', '', 'Doha'],
    ['AL NOOR  trading wll', 'dup@example.qa', '', 'Doha'],
    ['Gulf Paper Industries', 'not-an-email', '+974 4000 1300', 'Al Wakrah'],
  ],
};

/**
 * The same checks the server runs, for the development preview only.
 *
 * A second implementation of a rule is a second thing to keep in step, which
 * is why this is the only place it happens and why it is this short. The
 * server's version is the one with tests and the one that decides.
 */
export function checkImportLocally(raw: string[][], columns: Record<string, number>) {
  const fold = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');
  const emailish = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;
  const seen = new Set<string>();

  return raw.map((row, index) => {
    const fields: Record<string, string> = {};
    for (const [key, at] of Object.entries(columns)) fields[key] = (row[at] ?? '').trim();

    const problems: string[] = [];
    const name = fields.name ?? '';
    if (name === '') problems.push('no_name');
    else if (seen.has(fold(name))) problems.push('duplicate_in_file');
    else seen.add(fold(name));

    const email = fields.email ?? '';
    if (email !== '' && !emailish.test(email)) problems.push('bad_email');

    return { line: index + 2, fields, problems };
  });
}

/**
 * What the demo company has been doing. Small numbers on purpose: the screen
 * this replaced showed twelve pushes and three duplicates for every company,
 * and a fixture that looks busy is how that went unnoticed.
 */
export const DASHBOARD = {
  pushedToday: 2,
  pushedInvoices: 1,
  pushedBills: 1,
  pushedOther: 0,
  // Matches QUEUE.length, because the badge counts the queue and this
  // counts the same rows in the database. A demo that disagrees with itself
  // teaches people to ignore one of the two numbers.
  waiting: 4,
  blocked: 1,
  inReview: 1,
  failed: 0,
};

/* ---------------------------------------------------------------------------
   Reports.

   Invented figures for an invented company, as everything in this file is.
   Shaped to exercise the parts of each screen that are easy to get wrong:
   a negative line in the profit and loss, a customer sitting in more than one
   age bucket, an invoice with no due date, and a tax rate of zero alongside
   one that is not — which is the ordinary case for a Qatari company that also
   sells into the UAE.
--------------------------------------------------------------------------- */

const PROFIT_AND_LOSS_BASE = {
  source: 'xero' as const,
  currency: 'QAR',
  report: {
    titles: ['Profit and Loss', 'Al Nahda Trading WLL', '1 October 2026 to 15 October 2026'],
    reportDate: '15 October 2026',
    columns: ['15 Oct 2026'],
    sections: [
      {
        title: 'Income',
        rows: [
          { kind: 'row' as const, label: 'Sales', values: ['412,900.00'], accountId: '200' },
          { kind: 'row' as const, label: 'Other revenue', values: ['18,400.00'], accountId: '260' },
          { kind: 'total' as const, label: 'Total Income', values: ['431,300.00'], accountId: null },
        ],
      },
      {
        title: 'Less Cost of Sales',
        rows: [
          { kind: 'row' as const, label: 'Cost of goods sold', values: ['236,780.00'], accountId: '310' },
          { kind: 'total' as const, label: 'Total Cost of Sales', values: ['236,780.00'], accountId: null },
        ],
      },
      {
        title: 'Gross Profit',
        rows: [
          { kind: 'total' as const, label: 'Gross Profit', values: ['194,520.00'], accountId: null },
        ],
      },
      {
        title: 'Less Operating Expenses',
        rows: [
          { kind: 'row' as const, label: 'Advertising', values: ['9,250.00'], accountId: '400' },
          { kind: 'row' as const, label: 'Bank fees', values: ['1,050.00'], accountId: '404' },
          { kind: 'row' as const, label: 'General expenses', values: ['42,310.00'], accountId: '420' },
          { kind: 'row' as const, label: 'Kitchen consumables', values: ['17,640.00'], accountId: '429' },
          { kind: 'row' as const, label: 'Foreign exchange gain', values: ['-2,180.00'], accountId: '497' },
          { kind: 'total' as const, label: 'Total Operating Expenses', values: ['68,070.00'], accountId: null },
        ],
      },
      {
        title: '',
        rows: [
          { kind: 'total' as const, label: 'Net Profit', values: ['126,450.00'], accountId: null },
        ],
      },
    ],
  },
};

/**
 * The same report with comparison columns, so the chart and the extra
 * columns can be tried without Xero. Earlier months are the base figures
 * nudged, which is enough to see a trend without pretending to be real.
 */
export function profitAndLoss(periods: number) {
  if (periods <= 0) return { ...PROFIT_AND_LOSS_BASE, periods: 0, timeframe: null };

  const months = ['15 Oct 2026', '30 Sep 2026', '31 Aug 2026', '31 Jul 2026', '30 Jun 2026', '31 May 2026'];
  const columns = months.slice(0, periods + 1);
  const drift = [1, 0.94, 1.03, 0.88, 0.97, 1.08];

  return {
    ...PROFIT_AND_LOSS_BASE,
    periods,
    timeframe: 'MONTH',
    report: {
      ...PROFIT_AND_LOSS_BASE.report,
      columns,
      sections: PROFIT_AND_LOSS_BASE.report.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          values: columns.map((_, i) => {
            const base = Number((row.values[0] ?? '0').replace(/,/g, ''));
            return (base * (drift[i] ?? 1)).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          }),
        })),
      })),
    },
  };
}

export const PROFIT_AND_LOSS = { ...PROFIT_AND_LOSS_BASE, periods: 0, timeframe: null };

export const AGED_RECEIVABLES = {
  source: 'computed' as const,
  asAt: '2026-10-15',
  currency: 'QAR',
  rows: [
    {
      contactId: 'c1',
      contact: 'Corniche Hospitality Group',
      current: 48200,
      days1to30: 31400,
      days31to60: 0,
      days61to90: 0,
      days90plus: 0,
      total: 79600,
      documents: 4,
    },
    {
      contactId: 'c2',
      contact: 'Skyline Inflight Catering',
      current: 0,
      days1to30: 0,
      days31to60: 26800,
      days61to90: 0,
      days90plus: 14250,
      total: 41050,
      documents: 3,
    },
    {
      contactId: 'c3',
      contact: 'Souq Al Noor Consumer Goods',
      current: 12900,
      days1to30: 0,
      days31to60: 0,
      days61to90: 7400,
      days90plus: 0,
      total: 20300,
      documents: 2,
    },
  ],
  totals: {
    current: 61100,
    days1to30: 31400,
    days31to60: 26800,
    days61to90: 7400,
    days90plus: 14250,
    total: 140950,
  },
  undated: 1,
  documentsRead: 9,
  truncated: false,
};

export const AGED_PAYABLES = {
  source: 'computed' as const,
  asAt: '2026-10-15',
  currency: 'QAR',
  rows: [
    {
      contactId: 's1',
      contact: 'Gulf Cold Chain Logistics',
      current: 22400,
      days1to30: 8600,
      days31to60: 0,
      days61to90: 0,
      days90plus: 0,
      total: 31000,
      documents: 3,
    },
    {
      contactId: 's2',
      contact: 'Doha Packaging Supplies',
      current: 5100,
      days1to30: 0,
      days31to60: 3250,
      days61to90: 0,
      days90plus: 0,
      total: 8350,
      documents: 2,
    },
  ],
  totals: {
    current: 27500,
    days1to30: 8600,
    days31to60: 3250,
    days61to90: 0,
    days90plus: 0,
    total: 39350,
  },
  undated: 0,
  documentsRead: 5,
  truncated: false,
};

export const SALES_TAX = {
  source: 'computed' as const,
  from: '2026-10-01',
  to: '2026-10-15',
  currency: 'QAR',
  rows: [
    {
      taxType: 'OUTPUT',
      name: 'Tax on sales (5%)',
      netSales: 184000,
      taxOnSales: 9200,
      netPurchases: 0,
      taxOnPurchases: 0,
    },
    {
      taxType: 'INPUT',
      name: 'Tax on purchases (5%)',
      netSales: 0,
      taxOnSales: 0,
      netPurchases: 96400,
      taxOnPurchases: 4820,
    },
    {
      taxType: 'NONE',
      name: 'Tax exempt (0%)',
      netSales: 228900,
      taxOnSales: 0,
      netPurchases: 140380,
      taxOnPurchases: 0,
    },
  ],
  totals: {
    netSales: 412900,
    taxOnSales: 9200,
    netPurchases: 236780,
    taxOnPurchases: 4820,
  },
  net: 4380,
  documentsRead: 47,
  excluded: { drafts: 3, voided: 1 },
  truncated: false,
  basis: 'accrual' as const,
  excludes: ['manual journals'],
};

/* ---------------------------------------------------------------------------
   Training: catalogue, runs and bookings.

   Invented courses at an invented centre, as everything in this file is.
   Chosen to exercise what is easy to get wrong: a corporate engagement sold
   for one fee whose attendees carry no per-head price, a cohort with a
   cancelled seat and a discounted one, an uncapped online course, and a run
   whose dates have passed that nobody marked finished.

   Unlike the rest of this file these are mutable. The training screens are
   the first ones where the client creates records rather than reading them,
   and a demo where "Add a course" does nothing teaches the wrong thing about
   what the product does.
--------------------------------------------------------------------------- */

interface TrainingCourseFixture {
  courseId: number;
  name: string;
  nameAr: string | null;
  code: string | null;
  defaultFee: number | null;
  active: boolean;
  xeroItemCode: string | null;
  deliveries: number;
}

interface TrainingDeliveryFixture {
  deliveryId: number;
  courseId: number;
  courseName: string;
  defaultFee: number | null;
  name: string;
  code: string | null;
  kind: 'public' | 'corporate';
  startsOn: string;
  endsOn: string | null;
  trainer: string | null;
  venue: string | null;
  capacity: number | null;
  companyContactId: string | null;
  contractFee: number | null;
  status: 'planned' | 'running' | 'finished' | 'cancelled';
  notes: string | null;
  liveRegistrations: number;
  xeroInvoiceId: string | null;
  revenueAccountCode: string | null;
  closedAt: string | null;
}

export const TRAINING_COURSES: TrainingCourseFixture[] = [
  { courseId: 1, name: 'IFRS update, two days', nameAr: 'مستجدات المعايير الدولية، يومان', code: 'IFRS-2D', defaultFee: 1500, active: true, xeroItemCode: null, deliveries: 2 },
  { courseId: 2, name: 'VAT for the GCC', nameAr: 'ضريبة القيمة المضافة في الخليج', code: 'VAT-GCC', defaultFee: 1200, active: true, xeroItemCode: null, deliveries: 1 },
  { courseId: 3, name: 'Excel for finance teams', nameAr: 'إكسل لفرق المالية', code: 'XL-FIN', defaultFee: 850, active: true, xeroItemCode: null, deliveries: 1 },
  { courseId: 4, name: 'Bookkeeping basics', nameAr: 'أساسيات مسك الدفاتر', code: 'BK-101', defaultFee: 600, active: false, xeroItemCode: null, deliveries: 0 },
];

export const TRAINING_DELIVERIES: TrainingDeliveryFixture[] = [
  {
    deliveryId: 1, courseId: 1, courseName: 'IFRS update, two days', defaultFee: 1500,
    name: 'IFRS update — November cohort', code: 'IFRS-2611', kind: 'public' as const,
    startsOn: '2026-11-02', endsOn: '2026-11-03',
    trainer: 'Senior technical partner', venue: 'Lusail training room',
    capacity: 20, companyContactId: null, contractFee: null,
    status: 'planned' as const, notes: null, liveRegistrations: 3, xeroInvoiceId: null, revenueAccountCode: '200', closedAt: null,
  },
  {
    deliveryId: 2, courseId: 1, courseName: 'IFRS update, two days', defaultFee: 1500,
    name: 'IFRS update — in-house for a bank', code: 'IFRS-2610C', kind: 'corporate' as const,
    startsOn: '2026-10-14', endsOn: '2026-10-16',
    trainer: 'Senior technical partner', venue: "Client's offices",
    capacity: 12, companyContactId: 'c1', contractFee: 48000,
    status: 'running' as const, notes: 'Three sessions, invoiced on completion.',
    liveRegistrations: 2, xeroInvoiceId: null, revenueAccountCode: '200', closedAt: null,
  },
  {
    deliveryId: 3, courseId: 2, courseName: 'VAT for the GCC', defaultFee: 1200,
    name: 'VAT for the GCC — September', code: 'VAT-2609', kind: 'public' as const,
    startsOn: '2026-09-21', endsOn: '2026-09-21',
    trainer: 'Tax manager', venue: 'Lusail training room',
    capacity: 25, companyContactId: null, contractFee: null,
    // Dates long past and still marked planned, which is the ordinary state
    // of a busy office. The screen should read it from the calendar.
    status: 'planned' as const, notes: null, liveRegistrations: 2, xeroInvoiceId: null, revenueAccountCode: '200', closedAt: null,
  },
  {
    deliveryId: 4, courseId: 3, courseName: 'Excel for finance teams', defaultFee: 850,
    name: 'Excel for finance teams — online', code: 'XL-2611', kind: 'public' as const,
    startsOn: '2026-11-18', endsOn: null,
    trainer: 'Systems consultant', venue: 'Online',
    capacity: null, companyContactId: null, contractFee: null,
    status: 'planned' as const, notes: null, liveRegistrations: 1, xeroInvoiceId: null, revenueAccountCode: '200', closedAt: null,
  },
];

export const TRAINING_REGISTRATIONS: Record<number, TrainingRegistrationFixture[]> = {
  1: [
    { registrationId: 1, studentName: 'A. Rahman', studentEmail: 'student1@example.com', studentPhone: null, billContactId: 'c2', fee: null, discount: 0, status: 'registered', xeroInvoiceId: 'inv-9001', registeredOn: '2026-10-02' },
    { registrationId: 2, studentName: 'N. Haddad', studentEmail: 'student2@example.com', studentPhone: null, billContactId: 'c2', fee: 1500, discount: 250, status: 'registered', xeroInvoiceId: null, registeredOn: '2026-10-05' },
    { registrationId: 3, studentName: 'S. Mansoor', studentEmail: null, studentPhone: '+974 0000 0000', billContactId: null, fee: null, discount: 0, status: 'registered', xeroInvoiceId: null, registeredOn: '2026-10-09' },
    { registrationId: 4, studentName: 'K. Othman', studentEmail: null, studentPhone: null, billContactId: null, fee: null, discount: 0, status: 'cancelled', xeroInvoiceId: null, registeredOn: '2026-10-03' },
  ],
  2: [
    { registrationId: 5, studentName: 'Attendee one', studentEmail: null, studentPhone: null, billContactId: 'c1', fee: null, discount: 0, status: 'attended', xeroInvoiceId: null, registeredOn: '2026-10-01' },
    { registrationId: 6, studentName: 'Attendee two', studentEmail: null, studentPhone: null, billContactId: 'c1', fee: null, discount: 0, status: 'attended', xeroInvoiceId: null, registeredOn: '2026-10-01' },
  ],
  3: [
    { registrationId: 7, studentName: 'M. Idris', studentEmail: null, studentPhone: null, billContactId: null, fee: 1200, discount: 0, status: 'attended', xeroInvoiceId: 'inv-8801', registeredOn: '2026-09-10' },
    { registrationId: 8, studentName: 'R. Suleiman', studentEmail: null, studentPhone: null, billContactId: null, fee: 1200, discount: 0, status: 'attended', xeroInvoiceId: 'inv-8802', registeredOn: '2026-09-12' },
    // Withdrew and was refunded. The invoice still exists in Xero at its
    // full face value with a credit note against it, which is exactly the
    // case that used to make this course look more profitable than it was.
    { registrationId: 10, studentName: 'F. Nasser', studentEmail: null, studentPhone: null, billContactId: null, fee: 1200, discount: 0, status: 'refunded', xeroInvoiceId: 'inv-8803', registeredOn: '2026-09-14' },
  ],
  4: [
    { registrationId: 9, studentName: 'L. Farouk', studentEmail: null, studentPhone: null, billContactId: null, fee: null, discount: 0, status: 'registered', xeroInvoiceId: null, registeredOn: '2026-10-11' },
  ],
};

interface TrainingRegistrationFixture {
  registrationId: number;
  studentName: string;
  studentEmail: string | null;
  studentPhone: string | null;
  billContactId: string | null;
  fee: number | null;
  discount: number;
  status: 'registered' | 'attended' | 'cancelled' | 'refunded';
  xeroInvoiceId: string | null;
  registeredOn: string;
}

/** Next id for anything created in the demo, so new rows do not collide. */
let nextId = 100;

function withPhase<T extends { startsOn: string; endsOn: string | null; status: string }>(
  delivery: T,
) {
  return {
    ...delivery,
    phase: deliveryPhase(
      {
        deliveryId: 0,
        courseId: 0,
        name: '',
        kind: 'public',
        startsOn: delivery.startsOn,
        endsOn: delivery.endsOn,
        capacity: null,
        contractFee: null,
        status: delivery.status as 'planned' | 'running' | 'finished' | 'cancelled',
        defaultFee: null,
        xeroInvoiceId: null,
        companyContactId: null,
      },
      new Date(),
    ),
  };
}

export const TRAINING = {
  get currency() {
    return 'QAR';
  },
  get courses() {
    return TRAINING_COURSES;
  },
  get deliveries() {
    return TRAINING_DELIVERIES.map(withPhase);
  },
};

/**
 * Invented invoice states, so the payment column has all of its cases.
 *
 * One paid, one part paid, one overdue — the three a training centre
 * actually chases — rather than a column of identical green pills that
 * would never have shown the part-payment formatting was wrong.
 */
export const TRAINING_INVOICES: Record<string, InvoiceStateFixture> = {
  'inv-9001': { invoiceId: 'inv-9001', number: 'INV-0310', status: 'PAID', subTotal: 1500, total: 1500, amountDue: 0, amountPaid: 1500, amountCredited: 0, currencyCode: 'QAR', due: '2026-10-20' },
  'inv-8801': { invoiceId: 'inv-8801', number: 'INV-0288', status: 'PART PAID', subTotal: 1200, total: 1200, amountDue: 400, amountPaid: 800, amountCredited: 0, currencyCode: 'QAR', due: '2026-11-05' },
  'inv-8802': { invoiceId: 'inv-8802', number: 'INV-0289', status: 'OVERDUE', subTotal: 1200, total: 1200, amountDue: 1200, amountPaid: 0, amountCredited: 0, currencyCode: 'QAR', due: '2026-09-30' },
  /*
    A student who withdrew before paying, and whose invoice was credited.

    Here deliberately: this is the case that used to make a course look more
    profitable than it was, because Xero leaves SubTotal at the invoice's
    original value and records the credit separately. A demo without one
    would never have shown the figure was wrong.

    Note the shape. AmountPaid is zero and AmountCredited is the whole
    invoice, because Xero only allocates a credit note against a balance
    that is still owing — it cannot credit what has already been paid. Xero
    marks a fully credited invoice PAID, and the identity it maintains is
    Total = AmountPaid + AmountCredited + AmountDue.
  */
  'inv-8803': { invoiceId: 'inv-8803', number: 'INV-0291', status: 'PAID', subTotal: 1200, total: 1200, amountDue: 0, amountPaid: 0, amountCredited: 1200, currencyCode: 'QAR', due: '2026-10-02' },
};

interface InvoiceStateFixture {
  invoiceId: string;
  number: string;
  status: string;
  subTotal: number;
  total: number;
  amountDue: number;
  amountPaid: number;
  amountCredited: number;
  currencyCode: string;
  due: string | null;
}

function asDelivery(delivery: TrainingDeliveryFixture) {
  return {
    deliveryId: delivery.deliveryId,
    courseId: delivery.courseId,
    name: delivery.name,
    kind: delivery.kind,
    startsOn: delivery.startsOn,
    endsOn: delivery.endsOn,
    capacity: delivery.capacity,
    contractFee: delivery.contractFee,
    status: delivery.status,
    defaultFee: delivery.defaultFee,
    xeroInvoiceId: delivery.xeroInvoiceId,
    companyContactId: delivery.companyContactId,
  };
}

export function deliveryDetail(deliveryId: number) {
  const found = TRAINING_DELIVERIES.find((d) => d.deliveryId === deliveryId);
  const delivery = withPhase(found ?? TRAINING_DELIVERIES[0]!);
  const registrations = TRAINING_REGISTRATIONS[delivery.deliveryId] ?? [];

  const invoices: Record<string, InvoiceStateFixture> = {};
  for (const id of [...registrations.map((r) => r.xeroInvoiceId), delivery.xeroInvoiceId]) {
    if (id && TRAINING_INVOICES[id]) invoices[id] = TRAINING_INVOICES[id]!;
  }

  const costs = (TRAINING_COSTS[delivery.deliveryId] ?? []).map(costDetail);
  const credits = creditsForDelivery(delivery.deliveryId);
  const numbers = cohortNumbers(asDelivery(delivery), registrations);

  const result = deliveryResult(
    Object.values(invoices).map((invoice) => ({
      invoiceId: invoice.invoiceId,
      subTotal: invoice.subTotal,
      total: invoice.total,
      amountPaid: invoice.amountPaid,
      amountDue: invoice.amountDue,
      amountCredited: invoice.amountCredited,
      status: invoice.status,
      currencyCode: invoice.currencyCode,
    })),
    costs.map((cost) => ({
      costId: cost.costId,
      source: cost.source,
      xeroId: cost.xeroId,
      allocatedAmount: cost.allocatedAmount,
      subTotal: cost.subTotal,
      status: cost.missing ? 'VOIDED' : cost.status,
      currencyCode: cost.currencyCode,
    })),
    'QAR',
    credits.map((credit) => ({
      creditId: credit.creditId,
      xeroId: credit.xeroId,
      allocatedAmount: credit.allocatedAmount,
      subTotal: credit.subTotal,
      total: credit.total,
      allocatedToCohort: credit.allocatedToCohort,
      status: credit.missing ? 'VOIDED' : credit.status,
      currencyCode: credit.currencyCode,
    })),
  );

  return {
    currency: 'QAR',
    delivery,
    registrations,
    numbers,
    invoices,
    costs,
    credits,
    result,
    perSeat: delivery.kind === 'corporate' ? null : resultPerSeat(result, numbers.live),
    cannotInvoice: whyNothingToInvoice(asDelivery(delivery), registrations),
    suggestedInvoiceDate: defaultInvoiceDate(
      asDelivery(delivery),
      new Date().toISOString().slice(0, 10),
    ),
  };
}

/**
 * Bill the cohort, in the demo.
 *
 * Runs the real planner, so the demo shows the real behaviour: a corporate
 * booking produces one invoice to the company and a public cohort produces
 * one per unbilled student. Nothing reaches Xero.
 */
export function invoiceCohort(
  deliveryId: number,
  registrationIds: number[] | null,
  status: 'DRAFT' | 'AUTHORISED',
  date: string | null,
) {
  const today = new Date().toISOString().slice(0, 10);
  const found = TRAINING_DELIVERIES.find((d) => d.deliveryId === deliveryId);
  if (!found) return { created: [], failed: [], status, date: date ?? today };

  // The same default the server would apply, so the demo teaches the real
  // behaviour: a finished course is dated to the day it finished.
  const dated = date ?? defaultInvoiceDate(asDelivery(found), today);

  const registrations = TRAINING_REGISTRATIONS[deliveryId] ?? [];
  const course = TRAINING_COURSES.find((c) => c.courseId === found.courseId);

  const plan = planInvoices(asDelivery(found), registrations, {
    courseName: course?.name ?? '',
    revenueAccountCode: '200',
    taxType: null,
    only: registrationIds,
  });

  const created: { registrationId: number | null; invoiceId: string; total: number }[] = [];

  for (const planned of plan) {
    const invoiceId = `demo-${(nextId += 1)}`;
    TRAINING_INVOICES[invoiceId] = {
      invoiceId,
      number: `INV-${9000 + nextId}`,
      // A new invoice is awaiting payment, or a draft. Not paid: pretending
      // otherwise would teach that invoicing collects the money.
      status: status === 'DRAFT' ? 'DRAFT' : 'AWAITING PAYMENT',
      subTotal: planned.total,
      total: planned.total,
      amountDue: status === 'DRAFT' ? 0 : planned.total,
      amountPaid: 0,
      amountCredited: 0,
      currencyCode: 'QAR',
      due: null,
    };

    if (planned.registrationId === null) found.xeroInvoiceId = invoiceId;
    else {
      const seat = registrations.find((r) => r.registrationId === planned.registrationId);
      if (seat) seat.xeroInvoiceId = invoiceId;
    }

    created.push({ registrationId: planned.registrationId, invoiceId, total: planned.total });
  }

  return { created, failed: [], status, date: dated };
}

/**
 * Apply a demo write to the in-memory fixtures.
 *
 * Nothing is persisted and nothing reaches Xero — a reload starts over, which
 * the banner at the top of every screen already says. The point is only that
 * a button the client presses does the thing it says, so the demo teaches the
 * real shape of the product rather than a slideshow of it.
 */
/**
 * Put a demo spend on a course, the way the server does after Xero accepts
 * it. Without this the dropdown on the money-out form would appear to do
 * nothing, which teaches the wrong thing about what the product does.
 */
export function linkSpendToDelivery(deliveryId: number, xeroId: string, description: string): void {
  if (!AVAILABLE_COSTS.some((c) => c.xeroId === xeroId)) {
    AVAILABLE_COSTS.unshift({
      source: 'spend',
      xeroId,
      description,
      supplier: '',
      date: new Date().toISOString().slice(0, 10),
      status: 'AUTHORISED',
      subTotal: 0,
      total: 0,
      currencyCode: 'QAR',
      usedElsewhere: false,
    });
  }
  linkCost(deliveryId, { source: 'spend', xeroId, allocatedAmount: null, note: null });
}

export function saveTraining(path: string, body: unknown): void {
  const input = body as Record<string, unknown>;

  if (path === 'courses') {
    const existing = TRAINING_COURSES.find((c) => c.courseId === input.courseId);
    if (existing) {
      Object.assign(existing, {
        name: input.name,
        nameAr: input.nameAr,
        code: input.code,
        defaultFee: input.defaultFee,
        active: input.active,
      });
      return;
    }
    TRAINING_COURSES.push({
      courseId: (nextId += 1),
      name: String(input.name),
      nameAr: (input.nameAr as string) ?? null,
      code: (input.code as string) ?? null,
      defaultFee: (input.defaultFee as number) ?? null,
      active: input.active !== false,
      xeroItemCode: null,
      deliveries: 0,
    });
    return;
  }

  if (path === 'deliveries') {
    const course = TRAINING_COURSES.find((c) => c.courseId === input.courseId);
    const existing = TRAINING_DELIVERIES.find((d) => d.deliveryId === input.deliveryId);
    const fields = {
      courseId: Number(input.courseId),
      courseName: course?.name ?? '',
      defaultFee: course?.defaultFee ?? null,
      name: String(input.name),
      code: (input.code as string) ?? null,
      kind: input.kind as 'public' | 'corporate',
      startsOn: String(input.startsOn),
      endsOn: (input.endsOn as string) ?? null,
      trainer: (input.trainer as string) ?? null,
      venue: (input.venue as string) ?? null,
      capacity: (input.capacity as number) ?? null,
      companyContactId: (input.companyContactId as string) ?? null,
      contractFee: (input.contractFee as number) ?? null,
      status: input.status as 'planned' | 'running' | 'finished' | 'cancelled',
      notes: (input.notes as string) ?? null,
    };

    if (existing) {
      Object.assign(existing, fields);
      return;
    }

    const deliveryId = (nextId += 1);
    TRAINING_DELIVERIES.unshift({
      ...fields,
      deliveryId,
      liveRegistrations: 0,
      xeroInvoiceId: null,
      revenueAccountCode: '200',
      closedAt: null,
    });
    TRAINING_REGISTRATIONS[deliveryId] = [];
    if (course) course.deliveries += 1;
    return;
  }

  if (path === 'registrations') {
    const deliveryId = Number(input.deliveryId);
    const list = TRAINING_REGISTRATIONS[deliveryId] ?? [];
    TRAINING_REGISTRATIONS[deliveryId] = list;

    const existing = list.find((r) => r.registrationId === input.registrationId);
    const fields = {
      studentName: String(input.studentName),
      studentEmail: (input.studentEmail as string) ?? null,
      studentPhone: (input.studentPhone as string) ?? null,
      billContactId: (input.billContactId as string) ?? null,
      fee: (input.fee as number) ?? null,
      discount: Number(input.discount ?? 0),
      status: input.status as TrainingRegistrationFixture['status'],
    };

    if (existing) Object.assign(existing, fields);
    else {
      list.push({
        ...fields,
        registrationId: (nextId += 1),
        xeroInvoiceId: null,
        registeredOn: new Date().toISOString().slice(0, 10),
      });
    }

    const delivery = TRAINING_DELIVERIES.find((d) => d.deliveryId === deliveryId);
    if (delivery) {
      delivery.liveRegistrations = list.filter(
        (r) => r.status === 'registered' || r.status === 'attended',
      ).length;
    }
  }
}

/* ---------------------------------------------------------------------------
   Costs put on a course, and closing its books.

   Invented suppliers. Chosen so the result card has its awkward cases: a
   bill split across three courses, a cash expense from the money-out
   screen, and a cohort that loses money once its costs are counted.
--------------------------------------------------------------------------- */

interface CostFixture {
  costId: number;
  source: 'bill' | 'spend';
  xeroId: string;
  allocatedAmount: number | null;
  note: string | null;
  linkedAt: string;
}

export const AVAILABLE_COSTS = [
  { source: 'bill' as const, xeroId: 'b-1', description: 'BILL-0442', supplier: 'Gulf Print House', date: '2026-10-28', status: 'AUTHORISED', subTotal: 3600, total: 3780, currencyCode: 'QAR', usedElsewhere: false },
  { source: 'bill' as const, xeroId: 'b-2', description: 'BILL-0451', supplier: 'Lusail Venue Hire', date: '2026-11-01', status: 'AUTHORISED', subTotal: 4500, total: 4725, currencyCode: 'QAR', usedElsewhere: false },
  { source: 'bill' as const, xeroId: 'b-3', description: 'BILL-0455', supplier: 'A freelance trainer', date: '2026-11-03', status: 'AUTHORISED', subTotal: 6000, total: 6000, currencyCode: 'QAR', usedElsewhere: false },
  { source: 'spend' as const, xeroId: 's-1', description: 'Catering, two days', supplier: 'Corniche Hospitality Group', date: '2026-11-02', status: 'AUTHORISED', subTotal: 1850, total: 1942.5, currencyCode: 'QAR', usedElsewhere: false },
  { source: 'spend' as const, xeroId: 's-2', description: 'Taxis for the trainer', supplier: 'Karwa', date: '2026-11-03', status: 'AUTHORISED', subTotal: 220, total: 220, currencyCode: 'QAR', usedElsewhere: false },
  // Priced in another currency, so the mixed-currency notice has something
  // to be triggered by without inventing a second company.
  { source: 'bill' as const, xeroId: 'b-4', description: 'BILL-0460', supplier: 'An overseas awarding body', date: '2026-11-04', status: 'AUTHORISED', subTotal: 900, total: 900, currencyCode: 'USD', usedElsewhere: false },
];

export const TRAINING_COSTS: Record<number, CostFixture[]> = {
  1: [
    { costId: 1, source: 'bill', xeroId: 'b-1', allocatedAmount: 1200, note: 'A third of the print run', linkedAt: '2026-10-29T08:00:00.000Z' },
    { costId: 2, source: 'spend', xeroId: 's-1', allocatedAmount: null, note: null, linkedAt: '2026-11-02T08:00:00.000Z' },
  ],
  3: [
    { costId: 3, source: 'bill', xeroId: 'b-3', allocatedAmount: null, note: null, linkedAt: '2026-09-22T08:00:00.000Z' },
  ],
};

/**
 * What every course together claims of one document, from the demo links.
 *
 * The same aggregate the server reads from SQL, so putting the same bill
 * whole onto two courses in the demo produces the same warning it would in
 * production rather than looking fine.
 */
function claimFor(xeroId: string) {
  const links = Object.values(TRAINING_COSTS)
    .flat()
    .filter((link) => link.xeroId === xeroId);

  return {
    deliveries: links.length,
    allocatedSum: links.reduce((sum, link) => sum + (link.allocatedAmount ?? 0), 0),
    wholeCount: links.filter((link) => link.allocatedAmount === null).length,
  };
}

function costDetail(link: CostFixture) {
  const found = AVAILABLE_COSTS.find(
    (c) => c.xeroId === link.xeroId && c.source === link.source,
  );
  const subTotal = found?.subTotal ?? 0;
  const check = allocationCheck(claimFor(link.xeroId), subTotal);

  return {
    ...link,
    description: found?.description ?? '',
    supplier: found?.supplier ?? '',
    date: found?.date ?? null,
    status: found?.status ?? '',
    subTotal,
    total: found?.total ?? 0,
    currencyCode: found?.currencyCode ?? '',
    missing: found === undefined,
    onOtherCourses: check.onOtherCourses,
    claimedTotal: check.claimed,
    overAllocated: check.overAllocated,
  };
}

export function costOptions() {
  const used = new Set(
    Object.values(TRAINING_COSTS).flatMap((list) => list.map((c) => c.xeroId)),
  );
  return {
    currency: 'QAR',
    costs: AVAILABLE_COSTS.map((cost) => ({ ...cost, usedElsewhere: used.has(cost.xeroId) })),
  };
}

export function linkCost(
  deliveryId: number,
  cost: { source: 'bill' | 'spend'; xeroId: string; allocatedAmount: number | null; note: string | null },
): void {
  const list = TRAINING_COSTS[deliveryId] ?? [];
  TRAINING_COSTS[deliveryId] = list;

  const existing = list.find((c) => c.xeroId === cost.xeroId && c.source === cost.source);
  if (existing) {
    existing.allocatedAmount = cost.allocatedAmount;
    existing.note = cost.note;
    return;
  }

  list.push({
    costId: (nextId += 1),
    source: cost.source,
    xeroId: cost.xeroId,
    allocatedAmount: cost.allocatedAmount,
    note: cost.note,
    linkedAt: new Date().toISOString(),
  });
}

export function unlinkCost(deliveryId: number, costId: number): void {
  TRAINING_COSTS[deliveryId] = (TRAINING_COSTS[deliveryId] ?? []).filter(
    (c) => c.costId !== costId,
  );
}

/** Sell a course as a Xero product, in the demo. Runs the real code builder. */
export function setCourseItem(
  courseId: number,
  body: { existingCode?: string | null; unlink?: boolean },
): void {
  const course = TRAINING_COURSES.find((c) => c.courseId === courseId);
  if (!course) return;

  if (body.unlink) {
    course.xeroItemCode = null;
    return;
  }
  course.xeroItemCode = body.existingCode ?? itemCodeFor(course);
}

export function setDeliveryClosed(deliveryId: number, closed: boolean): void {
  const delivery = TRAINING_DELIVERIES.find((d) => d.deliveryId === deliveryId);
  if (delivery) delivery.closedAt = closed ? new Date().toISOString() : null;
}

/* ---------------------------------------------------------------------------
   What CFOAI gave this client, in the demo.

   Two switches, held here so the connection screen can be driven without an
   API: flipping one and watching the Training section appear or vanish from
   the sidebar is the whole behaviour, and a demo that cannot show it is a
   demo that cannot catch it going wrong.
--------------------------------------------------------------------------- */

const CLIENT_FEATURES: { training: boolean; xeroProjects: boolean; amountsAre: 'Exclusive' | 'Inclusive' | 'NoTax' } =
  { training: true, xeroProjects: false, amountsAre: 'NoTax' };

/** Everything the real endpoint grants, minus Projects until it is asked for. */
const GRANTED_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'accounting.invoices',
  'accounting.contacts',
  'accounting.settings',
  'accounting.payments',
];

export function connectionDetail() {
  const requested = CLIENT_FEATURES.xeroProjects
    ? [...GRANTED_SCOPES, 'projects']
    : [...GRANTED_SCOPES];
  const missing = requested.filter((scope) => !GRANTED_SCOPES.includes(scope));

  return {
    organisation: TENANTS[0]?.name ?? 'A client company',
    status: 'healthy',
    granted: [...GRANTED_SCOPES],
    requested,
    missing,
    needsReconnect: missing.length > 0,
    features: { ...CLIENT_FEATURES },
    accessToken: { expiresAt: null, expiresInMinutes: 24, valid: true },
    refreshToken: { rotatedAt: '2026-10-14', ageDays: 2, windowDays: 60, daysRemaining: 58 },
    lastRefreshAt: '2026-10-16T09:14:00.000Z',
    lastRefreshError: null,
    consecutiveFailures: 0,
  };
}

export function setOrganisationFeatures(change: {
  training?: boolean;
  xeroProjects?: boolean;
  amountsAre?: 'Exclusive' | 'Inclusive' | 'NoTax';
}) {
  if (change.training !== undefined) CLIENT_FEATURES.training = change.training;
  if (change.xeroProjects !== undefined) CLIENT_FEATURES.xeroProjects = change.xeroProjects;
  if (change.amountsAre !== undefined) CLIENT_FEATURES.amountsAre = change.amountsAre;

  const detail = connectionDetail();
  return {
    features: { ...CLIENT_FEATURES },
    missing: detail.missing,
    needsReconnect: detail.needsReconnect,
  };
}

/* ---------------------------------------------------------------------------
   Training setup, in the demo.

   The account list starts empty on purpose: that is what a real client
   looks like before anybody has said which accounts carry a course cost,
   and it is the state in which the flag must stay silent rather than
   listing every bill in the company.
--------------------------------------------------------------------------- */

const DIRECT_COST_ACCOUNTS: string[] = [];

export function trainingSetup() {
  return {
    directCostAccounts: [...DIRECT_COST_ACCOUNTS],
    tracking: {
      categories: [
        { id: 'tc-1', name: 'Training course', active: true, activeOptions: 3, ours: true },
      ],
      activeCategories: 1,
      slotsFree: 1,
      categoryId: 'tc-1',
      categoryName: 'Training course',
      budget: { active: 3, remaining: 97, full: false, nearlyFull: false },
    },
  };
}

export function saveDirectCostAccounts(accounts: string[]): void {
  DIRECT_COST_ACCOUNTS.length = 0;
  DIRECT_COST_ACCOUNTS.push(...accounts);
}

export function unaccountedBills() {
  if (DIRECT_COST_ACCOUNTS.length === 0) return { configured: false, bills: [] };

  const linked = new Set(
    Object.values(TRAINING_COSTS).flat().map((cost) => cost.xeroId),
  );

  // Invented account codes against the demo bills, so ticking an account
  // above actually changes what appears below.
  const codes: Record<string, string> = { 'b-1': '400', 'b-2': '405', 'b-3': '410', 'b-4': '400' };

  const bills = AVAILABLE_COSTS.filter(
    (cost) =>
      cost.source === 'bill' &&
      !linked.has(cost.xeroId) &&
      DIRECT_COST_ACCOUNTS.includes(codes[cost.xeroId] ?? ''),
  ).map((cost) => ({
    xeroId: cost.xeroId,
    description: cost.description,
    supplier: cost.supplier,
    date: cost.date,
    subTotal: cost.subTotal,
    currencyCode: cost.currencyCode,
    accountCodes: [codes[cost.xeroId] ?? ''],
  }));

  return { configured: true, bills, scanned: AVAILABLE_COSTS.length, truncated: false };
}

/* ---------------------------------------------------------------------------
   Money given back, in the demo.

   One credit note that stands alone — a student refunded after paying in
   full. That is the case this whole panel exists for, and a demo without
   one could never show the course figure dropping when it is linked.
--------------------------------------------------------------------------- */

interface CreditFixture {
  creditId: number;
  xeroId: string;
  allocatedAmount: number | null;
  note: string | null;
  linkedAt: string;
}

export const AVAILABLE_CREDITS = [
  { xeroId: 'cn-1', number: 'CN-0021', contact: 'M. Idris', date: '2026-10-05', status: 'AUTHORISED', subTotal: 1200, total: 1200, currencyCode: 'QAR', usedElsewhere: false },
  { xeroId: 'cn-2', number: 'CN-0024', contact: 'A. Rahman', date: '2026-10-12', status: 'AUTHORISED', subTotal: 750, total: 750, currencyCode: 'QAR', usedElsewhere: false },
];

export const TRAINING_CREDITS: Record<number, CreditFixture[]> = {};

export function creditOptions() {
  const used = new Set(
    Object.values(TRAINING_CREDITS).flat().map((credit) => credit.xeroId),
  );
  return {
    currency: 'QAR',
    credits: AVAILABLE_CREDITS.map((credit) => ({
      ...credit,
      usedElsewhere: used.has(credit.xeroId),
    })),
  };
}

function creditDetail(link: CreditFixture) {
  const found = AVAILABLE_CREDITS.find((c) => c.xeroId === link.xeroId);
  return {
    ...link,
    number: found?.number ?? '',
    contact: found?.contact ?? '',
    date: found?.date ?? null,
    status: found?.status ?? '',
    subTotal: found?.subTotal ?? 0,
    total: found?.total ?? 0,
    currencyCode: found?.currencyCode ?? '',
    // Nothing allocated in the demo: these are the standalone case.
    allocatedToCohort: 0,
    missing: found === undefined,
  };
}

export function linkCredit(
  deliveryId: number,
  credit: { xeroId: string; allocatedAmount: number | null; note: string | null },
): void {
  const list = TRAINING_CREDITS[deliveryId] ?? [];
  TRAINING_CREDITS[deliveryId] = list;

  const existing = list.find((c) => c.xeroId === credit.xeroId);
  if (existing) {
    existing.allocatedAmount = credit.allocatedAmount;
    return;
  }
  list.push({
    creditId: (nextId += 1),
    xeroId: credit.xeroId,
    allocatedAmount: credit.allocatedAmount,
    note: credit.note,
    linkedAt: new Date().toISOString(),
  });
}

export function unlinkCredit(deliveryId: number, creditId: number): void {
  const list = TRAINING_CREDITS[deliveryId];
  if (!list) return;
  TRAINING_CREDITS[deliveryId] = list.filter((credit) => credit.creditId !== creditId);
}

export function creditsForDelivery(deliveryId: number) {
  return (TRAINING_CREDITS[deliveryId] ?? []).map(creditDetail);
}

/* ---------------------------------------------------------------------------
   Balance sheet, and the aged detail behind one name.

   Both arrive from Xero in the same flattened shape as the profit and loss,
   so both are drawn by the same table. Invented figures, like everything
   else here — and they balance, because a demo balance sheet that does not
   is the first thing an accountant notices and the last thing they forgive.
--------------------------------------------------------------------------- */

const BALANCE_SHEET_BASE = {
  source: 'xero' as const,
  currency: 'QAR',
  periods: 0,
  timeframe: null,
  report: {
    titles: ['Balance Sheet', 'Al Nahda Trading WLL', 'As at 15 October 2026'],
    reportDate: '15 October 2026',
    columns: ['15 Oct 2026'],
    sections: [
      {
        title: 'Bank',
        rows: [
          { kind: 'row' as const, label: 'QNB current account', values: ['318,420.00'], accountId: '090' },
          { kind: 'row' as const, label: 'Petty cash', values: ['6,180.00'], accountId: '091' },
          { kind: 'total' as const, label: 'Total Bank', values: ['324,600.00'], accountId: null },
        ],
      },
      {
        title: 'Current Assets',
        rows: [
          { kind: 'row' as const, label: 'Accounts receivable', values: ['182,350.00'], accountId: '610' },
          { kind: 'row' as const, label: 'Inventory', values: ['94,700.00'], accountId: '630' },
          { kind: 'total' as const, label: 'Total Current Assets', values: ['277,050.00'], accountId: null },
        ],
      },
      {
        title: 'Fixed Assets',
        rows: [
          { kind: 'row' as const, label: 'Office equipment', values: ['86,000.00'], accountId: '710' },
          { kind: 'row' as const, label: 'Less accumulated depreciation', values: ['-23,450.00'], accountId: '711' },
          { kind: 'total' as const, label: 'Total Fixed Assets', values: ['62,550.00'], accountId: null },
        ],
      },
      {
        title: 'Total Assets',
        rows: [
          { kind: 'total' as const, label: 'Total Assets', values: ['664,200.00'], accountId: null },
        ],
      },
      {
        title: 'Current Liabilities',
        rows: [
          { kind: 'row' as const, label: 'Accounts payable', values: ['141,900.00'], accountId: '800' },
          { kind: 'row' as const, label: 'Accruals', values: ['18,300.00'], accountId: '825' },
          { kind: 'total' as const, label: 'Total Current Liabilities', values: ['160,200.00'], accountId: null },
        ],
      },
      {
        title: 'Total Liabilities',
        rows: [
          { kind: 'total' as const, label: 'Total Liabilities', values: ['160,200.00'], accountId: null },
        ],
      },
      {
        title: 'Equity',
        rows: [
          { kind: 'row' as const, label: 'Share capital', values: ['200,000.00'], accountId: '970' },
          { kind: 'row' as const, label: 'Retained earnings', values: ['109,480.00'], accountId: '960' },
          { kind: 'row' as const, label: 'Current year earnings', values: ['194,520.00'], accountId: null },
          { kind: 'total' as const, label: 'Total Equity', values: ['504,000.00'], accountId: null },
        ],
      },
    ],
  },
};

export function balanceSheet(date: string) {
  // Only the stated day changes with the picker; the figures are invented
  // and do not pretend to move, which the demonstration banner already says.
  return { ...BALANCE_SHEET_BASE, date };
}

export function agedDetail(direction: 'receivable' | 'payable', contactId: string, date: string) {
  const receivable = direction === 'receivable';
  const rows = receivable ? AGED_RECEIVABLES.rows : AGED_PAYABLES.rows;
  const who = rows.find((row) => row.contactId === contactId)?.contact ?? 'Unknown contact';

  return {
    source: 'xero' as const,
    currency: 'QAR',
    periods: 0,
    timeframe: null,
    date,
    report: {
      titles: [
        receivable ? 'Aged Receivables Detail' : 'Aged Payables Detail',
        who,
        `As at ${date}`,
      ],
      reportDate: date,
      columns: ['Due date', 'Total', 'Paid', 'Credited', 'Due'],
      sections: [
        {
          title: who,
          rows: [
            {
              kind: 'row' as const,
              label: receivable ? 'INV-0241' : 'BILL-0118',
              values: ['14 Sep 2026', '48,200.00', '0.00', '0.00', '48,200.00'],
              accountId: null,
            },
            {
              kind: 'row' as const,
              label: receivable ? 'INV-0263' : 'BILL-0126',
              values: ['02 Oct 2026', '31,400.00', '0.00', '0.00', '31,400.00'],
              accountId: null,
            },
            {
              kind: 'total' as const,
              label: 'Total',
              values: ['', '79,600.00', '0.00', '0.00', '79,600.00'],
              accountId: null,
            },
          ],
        },
      ],
    },
  };
}
